import { assignInlineVars } from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { IRetailerUserNotification } from "@faire/web-api--source/indigofair/retailer/IRetailerUserNotification";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { useMemo } from "react";

import { toastTopVar, topVar, transitionDurationVar } from "./consts.css";

const HIDE_COMPONENT_OFFSET_TOP = 300;

type Arg = {
  nonStickyHeader: boolean;
  topPosition: number;
  notificationPreview: IRetailerUserNotification | undefined;
  isNotificationPreviewVisible: boolean;
  shouldRetainOriginalPosition: boolean;
  shouldShowSaleAnnouncementTooltip?: boolean;
  contentRef: React.RefObject<HTMLDivElement | null>;
};

export const useStyle = ({
  nonStickyHeader,
  topPosition,
  notificationPreview,
  isNotificationPreviewVisible,
  shouldRetainOriginalPosition,
  shouldShowSaleAnnouncementTooltip,
  contentRef,
}: Arg) => {
  const { isDesktopAndAbove } = useViewport();

  return useMemo((): React.CSSProperties | undefined => {
    if (!nonStickyHeader) {
      // AddedToBag must not get unmounted when the `hideHeadersAndBanners` prop
      // changes, or otherwise we don't correctly show the added to bag toast
      // after adding a product to cart from the mobile product modal.

      // Preview has an absolute position, so we need to account for it
      const notificationPreviewOffset =
        notificationPreview && isNotificationPreviewVisible ? 100 : 0;

      const salesTooltipOffset = shouldShowSaleAnnouncementTooltip ? 188 : 0;

      const absoluteTopElementsOffset =
        notificationPreviewOffset + salesTooltipOffset;

      const stickyContentHeight = contentRef.current?.clientHeight
        ? contentRef.current.clientHeight + absoluteTopElementsOffset
        : undefined;
      const hideComponentOffsetTop =
        stickyContentHeight ?? HIDE_COMPONENT_OFFSET_TOP;
      return assignInlineVars({
        [topVar.variable]: shouldRetainOriginalPosition
          ? `${topPosition}px`
          : `-${hideComponentOffsetTop}px`,
        [transitionDurationVar.variable]: `${
          (hideComponentOffsetTop / HIDE_COMPONENT_OFFSET_TOP) * 400
        }ms`,
        [toastTopVar.variable]: shouldRetainOriginalPosition
          ? `${topPosition + hideComponentOffsetTop}px`
          : "0px",
      });
    }
    return undefined;
  }, [
    contentRef,
    isDesktopAndAbove,
    isNotificationPreviewVisible,
    nonStickyHeader,
    notificationPreview,
    shouldRetainOriginalPosition,
    shouldShowSaleAnnouncementTooltip,
  ]);
};
